import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { Page, PageHeader } from "@dash/page";
import { useTheme } from "@emotion/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MdDownload, MdOpenInNew, MdRefresh, MdUpload } from "react-icons/md";

import { ConfirmExecutionDialog } from "./ConfirmExecutionDialog";
import { DependentRefreshDialog } from "./DependentRefreshDialog";
import { DiffSyncErrorsTable } from "./DiffSyncErrorsTable";
import { DiffSyncTable } from "./DiffSyncTable";
import { DiffTabs } from "./DiffTabs";
import { DiffSyncExecutionFailure, ExecutionFailureDialog } from "./ExecutionFailureDialog";
import { formatErrorMessage } from "./formatErrorMessage";
import { DiffSyncDependentChannelRefreshRow, DiffSyncDirection, DiffSyncPageProps } from "./types";

export type {
  DiffSyncDependentChannelLink,
  DiffSyncDependentChannelRefreshResult,
  DiffSyncDirection,
  DiffSyncPageProps,
  DiffSyncRecord,
  ValueFormatter,
} from "./types";

export const DiffSyncPage = <T extends Record<string, unknown>>(props: DiffSyncPageProps<T>) => {
  const theme = useTheme();
  const { mq } = useDeviceType();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [activeTab, setActiveTab] = useState<DiffSyncDirection>("pull");
  const [confirmation, setConfirmation] = useState<{
    direction: DiffSyncDirection;
    diffIds: string[];
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [dependentRefresh, setDependentRefresh] = useState<{
    isRefreshing: boolean;
    rows: DiffSyncDependentChannelRefreshRow[];
  } | null>(null);
  const [executionFailure, setExecutionFailure] = useState<DiffSyncExecutionFailure | null>(null);
  const [selectedDiffIds, setSelectedDiffIds] = useState<Record<DiffSyncDirection, string[]>>({
    pull: [],
    push: [],
  });
  const activeDiffs = activeTab === "pull" ? props.pullableDiffs : props.pushableDiffs;
  const activeDiffIds = useMemo(() => (activeDiffs ?? []).map((diff) => diff.diffId), [activeDiffs]);
  const activeDiffIdSet = useMemo(() => new Set(activeDiffIds), [activeDiffIds]);
  const activeSelectedDiffIds = selectedDiffIds[activeTab].filter((id) => activeDiffIdSet.has(id));
  const directionLabels = {
    pull: props.directionLabels?.pull ?? "Pull",
    push: props.directionLabels?.push ?? "Push",
  };
  const isActionBusy = props.isRefreshing || props.isExecuting || dependentRefresh?.isRefreshing;
  const isExecuteDisabled = props.isLoading || isActionBusy || activeSelectedDiffIds.length === 0;

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const updateHeight = () => setHeaderHeight(header.offsetHeight);
    const resizeObserver = new ResizeObserver(updateHeight);

    updateHeight();
    resizeObserver.observe(header);
    window.addEventListener("resize", updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  useEffect(() => {
    setSelectedDiffIds((current) => ({
      ...current,
      pull: props.pullableDiffs?.map((diff) => diff.diffId) ?? [],
    }));
  }, [props.pullableDiffs]);

  useEffect(() => {
    setSelectedDiffIds((current) => ({
      ...current,
      push: props.pushableDiffs?.map((diff) => diff.diffId) ?? [],
    }));
  }, [props.pushableDiffs]);

  const execute = async (direction: DiffSyncDirection, diffIds: string[]) => {
    if (direction === "pull") await props.onPull(diffIds);
    if (direction === "push") await props.onPush(diffIds);
  };

  const confirmExecute = async () => {
    if (!confirmation || isConfirming) return;

    const executedDirection = confirmation.direction;
    const dependentChannels = getDependentChannels(props, executedDirection);

    setIsConfirming(true);
    setExecutionFailure(null);
    try {
      await execute(confirmation.direction, confirmation.diffIds);
      setConfirmation(null);

      const refreshDependentChannel = props.onRefreshDependentChannel;

      if (dependentChannels.length > 0 && refreshDependentChannel) {
        setDependentRefresh({
          isRefreshing: true,
          rows: dependentChannels.map((channel) => ({ ...channel, isLoading: true })),
        });

        await Promise.all(
          dependentChannels.map(async (channel) => {
            try {
              const result = await refreshDependentChannel(channel);
              updateDependentRefreshRow(channel.channel, { ...channel, ...result, isLoading: false });
            } catch (error) {
              updateDependentRefreshRow(channel.channel, {
                ...channel,
                isLoading: false,
                error: formatErrorMessage(error),
              });
            }
          }),
        );

        setDependentRefresh((current) => (current ? { ...current, isRefreshing: false } : current));
      }
    } catch (error) {
      setConfirmation(null);
      setExecutionFailure(getExecutionFailure(error, directionLabels[executedDirection]));
    } finally {
      setIsConfirming(false);
    }
  };

  const setSelection = (direction: DiffSyncDirection) => (diffIds: string[]) => {
    setSelectedDiffIds((current) => ({ ...current, [direction]: diffIds }));
  };

  const updateDependentRefreshRow = (channel: string, row: DiffSyncDependentChannelRefreshRow) => {
    setDependentRefresh((current) =>
      current
        ? {
            ...current,
            rows: current.rows.map((currentRow) => (currentRow.channel === channel ? row : currentRow)),
          }
        : current,
    );
  };

  return (
    <Page css={{ maxWidth: "100%", display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div
        ref={headerRef}
        css={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          padding: "0.75rem 1rem",
          background: "white",
          borderBottom: `1px solid ${theme.colors.border}`,
          [mq("desktop")]: {
            left: "var(--dash-nav-width, 260px)",
            padding: "0.75rem 2rem",
          },
        }}
      >
        <div css={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
          <PageHeader css={{ margin: 0 }}>{props.title}</PageHeader>
          <div css={{ flexGrow: 1 }} />
          {props.remoteResourceUrl && (
            <a href={props.remoteResourceUrl} target="_blank" rel="noreferrer" css={{ textDecoration: "none" }}>
              <Button as="div" variant="outline" icon={<MdOpenInNew />}>
                Otwórz arkusz
              </Button>
            </a>
          )}
          <Button
            variant="outline"
            icon={<MdRefresh />}
            onClick={() => void props.onRefresh()}
            isLoading={props.isRefreshing}
            isDisabled={isActionBusy}
          >
            Odśwież
          </Button>
          <Button
            icon={activeTab === "pull" ? <MdDownload /> : <MdUpload />}
            onClick={() => setConfirmation({ direction: activeTab, diffIds: activeSelectedDiffIds })}
            isLoading={props.isExecuting}
            isDisabled={isExecuteDisabled}
          >
            {directionLabels[activeTab]}
          </Button>
        </div>

        <DiffTabs
          activeTab={activeTab}
          directionLabels={directionLabels}
          pullCount={props.pullableDiffs?.length}
          pushCount={props.pushableDiffs?.length}
          onChange={setActiveTab}
        />
      </div>
      <div css={{ height: headerHeight, flexShrink: 0 }} />

      <DiffSyncErrorsTable
        diffs={props.errorDiffs}
        labels={props.labels}
        formatters={props.formatters}
        alwaysVisibleColumns={props.alwaysVisibleColumns}
        isLoading={props.isLoading}
      />

      <DiffSyncTable
        direction={activeTab}
        diffs={activeDiffs}
        selectedDiffIds={selectedDiffIds[activeTab]}
        onSelectionChange={setSelection(activeTab)}
        labels={props.labels}
        formatters={props.formatters}
        alwaysVisibleColumns={props.alwaysVisibleColumns}
        isLoading={props.isLoading}
      />

      {confirmation && (
        <ConfirmExecutionDialog
          directionLabel={directionLabels[confirmation.direction]}
          diffCount={confirmation.diffIds.length}
          isExecuting={props.isExecuting || isConfirming}
          onClose={() => setConfirmation(null)}
          onConfirm={() => void confirmExecute()}
        />
      )}

      {dependentRefresh && (
        <DependentRefreshDialog
          isRefreshing={dependentRefresh.isRefreshing}
          rows={dependentRefresh.rows}
          onClose={() => setDependentRefresh(null)}
        />
      )}

      {executionFailure && (
        <ExecutionFailureDialog failure={executionFailure} onClose={() => setExecutionFailure(null)} />
      )}
    </Page>
  );
};

const getDependentChannels = <T extends Record<string, unknown>>(
  props: DiffSyncPageProps<T>,
  direction: DiffSyncDirection,
) => (direction === "pull" ? props.pullDependentChannels : props.pushDependentChannels) ?? [];

const getExecutionFailure = (error: unknown, directionLabel: string): DiffSyncExecutionFailure => {
  if (isDiffsOutOfDateError(error)) {
    return {
      kind: "diffsOutOfDate",
      title: "Lista zmian jest nieaktualna",
      message: (
        <>
          Przed wykonaniem operacji diff-sync odświeżył kanał i wykrył, że zaznaczone zmiany się zmieniły. Aktualna
          lista zmian jest już załadowana. Wybierz zmiany ponownie i kliknij „{directionLabel}”.
        </>
      ),
    };
  }

  return {
    kind: "error",
    title: "Nie udało się wykonać operacji",
    message: formatErrorMessage(error),
  };
};

const isDiffsOutOfDateError = (error: unknown) => {
  if (error === "DiffsOutOfDate") return true;
  if (error instanceof Error && error.message === "DiffsOutOfDate") return true;
  if (typeof error === "object" && error !== null && "error" in error) {
    return (error as { error?: unknown }).error === "DiffsOutOfDate";
  }

  return false;
};
